import React, { useState, useEffect } from 'react'
import Slider from 'rc-slider'
import 'rc-slider/assets/index.css'

import PropTypes from 'prop-types'
import { isRTL } from '@s4l/utils/library/rtl'
import { InputSection, RangeWrapper, Histogram, HistogramBar } from './styles'
import InputLine from 'client/components/InputLine'
import PriceRangeHistogramPlaceholder from './PriceRangeHistogramPlaceholder'
import { useMedia } from 'react-use'
import { breakpoint } from 'client/styles'
import { useDevice } from 'src/hooks/useDevice'
import { parseDecimal } from './utils'

const PriceRangeHistogram = ({ isLoading, distribution = [], max = 0, min = 0, onChange }) => {
  const [innerMin, setInnerMin] = useState(min)
  const [innerMax, setInnerMax] = useState(max)
  const [histogram, setHistogram] = useState([])
  const [lowerBound, setLowerBound] = useState(0)
  const [upperBound, setUpperBound] = useState(0)
  const [maxDistributionCount, setMaxDistributionCount] = useState(0)
  const [minInputValue, setMinInputValue] = useState(0)
  const [maxInputValue, setMaxInputValue] = useState(0)

  const { device } = useDevice()
  const isWide = useMedia(breakpoint.tablet, device === 'desktop') && device !== 'tablet'

  function calculateRangeDistance(priceRanges) {
    return priceRanges[1].min - priceRanges[0].min
  }

  function buildHistogramBar(priceRange) {
    return {
      value: priceRange.min,
      count: priceRange.count,
      valueToDisplay: `${priceRange.min}`,
    }
  }

  function buildLastHistogramBar(priceRanges) {
    const rangeDistance = calculateRangeDistance(priceRanges)
    const lastPriceRange = { ...priceRanges[priceRanges.length - 1] }

    return {
      value: lastPriceRange.min + rangeDistance,
      count: lastPriceRange.count,
      valueToDisplay: `+${lastPriceRange.min}`,
    }
  }

  function buildHistogram(priceRanges) {
    return [...priceRanges.map(p => buildHistogramBar(p)), buildLastHistogramBar(priceRanges)]
  }

  function getClosestHistogramItem(value) {
    return histogram.reduce((prev, h) =>
      Math.abs(h.value - value) < Math.abs(prev.value - value) ? h : prev
    )
  }

  useEffect(() => {
    if (distribution.length < 2) {
      return
    }

    const priceHistogram = buildHistogram(distribution)

    setHistogram(priceHistogram)
    setLowerBound(priceHistogram[0].value)
    setUpperBound(priceHistogram[priceHistogram.length - 1].value)
    setMaxDistributionCount(
      priceHistogram.reduce(
        (maxCount, current) => (maxCount < current.count ? current.count : maxCount),
        0
      )
    )
  }, [distribution]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (histogram.length < 2) {
      return
    }

    const newMin = getClosestHistogramItem(min || lowerBound)
    const newMax = getClosestHistogramItem(max || upperBound)

    setInnerMin(newMin.value)
    setInnerMax(newMax.value)

    setMinInputValue(newMin.valueToDisplay)
    setMaxInputValue(newMax.valueToDisplay)
  }, [histogram, min, max]) // eslint-disable-line react-hooks/exhaustive-deps

  function getSelectedRange(minValue, maxValue) {
    return histogram.filter(d => d.value >= minValue && d.value <= maxValue)
  }

  function getRangeCount(minValue, maxValue) {
    const range = getSelectedRange(minValue, maxValue)

    return range.slice(0, range.length - 1).reduce((total, d) => total + d.count, 0)
  }

  function isValidRange(minValue, maxValue) {
    if (getRangeCount(minValue, maxValue) === 0) {
      return false
    }

    if (minValue >= maxValue) {
      return false
    }

    return true
  }

  function getMaxValid(value) {
    const newMaxValue = getClosestHistogramItem(value).value

    return isValidRange(innerMin, newMaxValue) ? newMaxValue : innerMax
  }

  function getMinValid(value) {
    const newMinValue = getClosestHistogramItem(value).value

    return isValidRange(newMinValue, innerMax) ? newMinValue : innerMin
  }

  function getValueToDisplayFromValue(value) {
    return histogram.find(h => h.value === value)?.valueToDisplay
  }

  function setSlideSelectedValues(newMinValue, newMaxValue) {
    setInnerMin(newMinValue)
    setInnerMax(newMaxValue)
    setMinInputValue(getValueToDisplayFromValue(newMinValue))
    setMaxInputValue(getValueToDisplayFromValue(newMaxValue))
  }

  function executeSearch(newMinValue, newMaxValue) {
    onChange(
      newMinValue === 0 ? null : parseDecimal(newMinValue),
      newMaxValue === upperBound ? null : parseDecimal(newMaxValue)
    )
  }

  function onSlideRangeBlur([newMin, newMax]) {
    executeSearch(newMin, newMax)
  }

  function onInputBlur(rawMinValue, rawMaxValue) {
    const newMinValue = getMinValid(rawMinValue)
    const newMaxValue = getMaxValid(rawMaxValue)

    setSlideSelectedValues(newMinValue, newMaxValue)

    executeSearch(newMinValue, newMaxValue)
  }

  function onRangeMove(newMin, newMax) {
    setSlideSelectedValues(getMinValid(newMin), getMaxValid(newMax))
  }

  function shouldDisplayPlaceholder() {
    return isLoading || (lowerBound === 0 && upperBound === 0) || histogram.length < 2
  }

  if (shouldDisplayPlaceholder()) {
    return (
      <PriceRangeHistogramPlaceholder
        min={min}
        max={max || upperBound}
        lowerBound={lowerBound}
        upperBound={upperBound}
      />
    )
  }

  return (
    <>
      <RangeWrapper isLoading={isLoading}>
        <Histogram>
          {histogram.slice(0, histogram.length - 1).map(bar => (
            <HistogramBar key={bar.value} percentage={(bar.count * 100) / maxDistributionCount} />
          ))}
        </Histogram>

        <Slider
          range
          min={lowerBound}
          max={upperBound}
          allowCross={false}
          value={[innerMin, innerMax]}
          disabled={isLoading}
          onChangeComplete={onSlideRangeBlur}
          reverse={isRTL()}
          onChange={r => onRangeMove(r[0], r[1])}
        />
      </RangeWrapper>

      <InputSection isLoading={isLoading}>
        <InputLine
          label="Min."
          value={`${minInputValue}`}
          type={isWide ? 'text' : 'number'}
          inputmode="numeric"
          pattern="[0-9]*"
          name="minPrice"
          onChange={e => setMinInputValue(e.target.value)}
          onBlur={e => onInputBlur(e.target.value, innerMax)}
          disabled={isLoading}
        />
        <InputLine
          label="Max."
          value={isWide ? `${maxInputValue}` : `${maxInputValue}`.replace(/\+/g, '')}
          type={isWide ? 'text' : 'number'}
          inputmode="numeric"
          pattern="[0-9]*"
          name="maxPrice"
          onChange={e => setMaxInputValue(e.target.value.replace(/\+/g, ''))}
          onBlur={e => onInputBlur(innerMin, e.target.value)}
          disabled={isLoading}
        />
      </InputSection>
    </>
  )
}

PriceRangeHistogram.propTypes = {
  isLoading: PropTypes.bool,
  distribution: PropTypes.arrayOf(
    PropTypes.shape({ min: PropTypes.number, count: PropTypes.number })
  ),
  max: PropTypes.number,
  min: PropTypes.number,
  onChange: PropTypes.func.isRequired,
}

export default PriceRangeHistogram
